import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useCommunicationService } from 'app/services/communication';
import { type AuthCode, decodeAuthCode } from 'app/utils/in-app-comunication/auth-code';
import { MessageType } from 'app/utils/in-app-comunication/types';
import useServices from 'app/services';
import useConfig from 'app/config';
import { useDispatch, useAppState } from 'state';
import { signin } from 'actions/auth';
import { useCookieConsent } from 'app/hooks/use-cookie-consent';
import HyroxLogo from 'src/app/ui/components/atoms/icons/hyrox-logo';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.black};
`;

const LogoContainer = styled.div`
  width: 50%;
`;

const AuthListener = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const comms = useCommunicationService();
  const services = useServices();
  const { config } = useConfig();
  const signedIn = useAppState(({ auth }) => auth.signedIn);
  const { hasConsentBeenSet, acceptAll } = useCookieConsent();

  const exchangeAuthCodeForToken = async ({ code, codeVerifier }: AuthCode) => {
    if (signedIn) {
      navigate('/');
      return;
    }
    try {
      const tokenResponse = await services.auth.exchangeCodeForToken({
        code,
        redirectUri: config.REACT_APP_AUTH_CALLBACK_REDIRECT_URL,
        ...(codeVerifier ? { codeVerifier } : {}),
      });

      // Dispatch signin action to update auth state
      dispatch(
        signin({
          accessToken: tokenResponse.accessToken,
          accessTokenExpiresAt: tokenResponse.accessTokenExpiresAt,
          refreshToken: tokenResponse.refreshToken,
          refreshTokenExpiresAt: tokenResponse.refreshTokenExpiresAt,
        }),
      );
    } catch (error) {
      console.error('Token exchange failed:', error);
    }
  };

  // Auto-accept cookies on auth-listener page
  useEffect(() => {
    if (!hasConsentBeenSet) {
      acceptAll();
    }
  }, [hasConsentBeenSet, acceptAll]);

  useEffect(() => {
    if (signedIn) {
      navigate('/');
    }
  }, [signedIn]);

  useEffect(() => {
    const unsubscribe = comms.onMessage((message) => {
      if (message.type === MessageType.AUTH_CODE_RECEIVED && message.authCode) {
        const decoded = decodeAuthCode(message.authCode);

        if (decoded) {
          // Automatically exchange the auth code for a token
          exchangeAuthCodeForToken(decoded);
        }
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
    // signedIn has to be a dependency: the handler would otherwise keep the value it was
    // registered with and re-exchange an already-spent, single-use code after signing in.
  }, [comms, signedIn]);

  return (
    <Container>
      <LogoContainer>
        <HyroxLogo />
      </LogoContainer>
    </Container>
  );
};

export default AuthListener;
